import { Box } from '@chakra-ui/react';
import { BenefitsSection } from 'components/BenefitsSection';
import Layout from 'components/Layout';
import { ProductCardGrid } from 'components/ProductCardGrid';
import { SEO } from 'components/SEO';
import SolutionSlider from 'components/SolutionSlider';
import StatsBar from 'components/StatsBar';
import { TestimonialSlider } from 'components/TestimonialSlider';
import { TwoColumnWithImage } from 'components/TwoColumnWithImage';
import VideoHero from 'components/VideoHero';
import { graphql } from 'gatsby';
import React, { useEffect } from 'react';

import { track } from '../utils/analytics/track';
import { ANALYTICS } from '../utils/constants/analytics';

export const Head = () => <SEO />;
const Home: React.FC<{ data: any }> = ({ data }) => {
  useEffect(() => {
    track({
      event: ANALYTICS.EVENTS.VIEWED_EHS_PAGE,
      data: {
        label: 'EHS Homepage',
      },
    });
  }, []);

  return (
    <Layout>
      <VideoHero {...data.contentfulPage001.hero} />
      <Box
        sx={{
          transform: ['translateY(0)', 'translateY(0)', 'translateY(-45%)'],
        }}
        px="1rem"
      >
        <StatsBar
          stats={
            data.contentfulPage001?.sections[0]?.container[0].statsCollection
          }
          disclaimer={data.contentfulPage001?.sections[0]?.disclaimer}
          backgroundColor={data.contentfulPage001?.sections[0]?.backgroundColor}
          textColor={
            data.contentfulPage001?.sections[0]?.container[0].textColor
          }
        />
      </Box>
      <BenefitsSection
        features={data.contentfulPage001?.sections[1]?.container}
      />
      <SolutionSlider {...data.contentfulPage001?.sections[2]} />
      <ProductCardGrid
        title={data.contentfulPage001?.sections[3].title}
        cards={data.contentfulPage001?.sections[3].container}
        sectionHeader={data.contentfulPage001?.sections[3]}
        showOverlay={true}
      />
      <TwoColumnWithImage
        {...data.contentfulPage001?.sections[4].container[0]}
      />
      <TwoColumnWithImage
        {...data.contentfulPage001?.sections[5].container[0]}
      />
      <TestimonialSlider
        testimonials={data.contentfulPage001?.sections[6].container}
      />
    </Layout>
  );
};

export default Home;

export const data = graphql`
  query HomepageQuery {
    contentfulPage001(displayName: { eq: "EHS Homepage" }) {
      displayName
      hero {
        ... on ContentfulVideoHero {
          richHeading: heading {
            raw
          }
          video {
            url
          }
          cta {
            text
            url
          }
          text
          secondaryImage {
            file {
              url
            }
          }
        }
      }
      sections {
        title
        headline
        backgroundColor
        description {
          childMarkdownRemark {
            rawMarkdownBody
          }
        }
        disclaimer {
          childMarkdownRemark {
            rawMarkdownBody
            html
          }
        }
        container {
          ... on ContentfulStatsBar {
            backgroundColor
            textColor
            statsCollection {
              suffix
              label
              value
              prefix
            }
          }
          ... on ContentfulServiceFeature {
            description
            featureImage {
              url
            }
          }
          ... on ContentfulSlider {
            slides {
              slideHeading: heading
              description {
                childMarkdownRemark {
                  rawMarkdownBody
                }
              }
              backgroundImage {
                file {
                  url
                }
              }
              mobileBackgroundImage {
                file {
                  url
                }
              }
              ctaButton {
                url
                label
              }
              features {
                icon {
                  url
                }
                text
              }
              navigationLabel
            }
          }
          ... on ContentfulHealthSolutionCard {
            icon {
              url
            }
            title
          }
          ... on ContentfulTwoColumnsWithImage {
            cta {
              url
              label
            }
            eyebrow
            heading
            image {
              file {
                url
              }
            }
            imageLeft
            text
          }
          ... on ContentfulTestimonial200 {
            authorName
            authorTitle
            quote {
              childMarkdownRemark {
                rawMarkdownBody
              }
            }
            image {
              file {
                url
              }
            }
          }
        }
      }
    }
  }
`;
